import { useAtom, useAtomValue } from 'jotai';
import { useCallback, useEffect } from 'react';

import { auth } from '@/services/firebase';
import { subscribeToStarred, toggleStar, updateStarredEdition } from '@/services/starred';
import type { StarredCard } from '@/types/lists';

import { starredOracleIdsAtom, starredStateAtom } from '../state/starredAtom';

/**
 * Sync starred cards from Firestore when authenticated.
 * Returns loading state.
 */
export function useStarredSync(isAuthenticated: boolean): boolean {
  const [{ loading }, setState] = useAtom(starredStateAtom);

  useEffect(() => {
    if (!isAuthenticated || !auth.currentUser) {
      setState({ data: new Map(), loading: false });
      return;
    }

    const unsubscribe = subscribeToStarred(auth.currentUser.uid, (data) => {
      setState({ data, loading: false });
    });

    return unsubscribe;
  }, [isAuthenticated, setState]);

  return loading;
}

/**
 * Hook to toggle star status for a card.
 * Uses optimistic updates for instant feedback.
 * Returns a stable function reference (no dependency on starred data).
 */
export function useToggleStar() {
  const [, setState] = useAtom(starredStateAtom);

  return useCallback(
    async (oracleId: string, name: string, scryfallId?: string) => {
      const uid = auth.currentUser?.uid;
      if (!uid) return;

      // Read current state at call time via setState to avoid closing over `data`
      let isCurrentlyStarred = false;

      // Optimistic update
      setState((prev) => {
        isCurrentlyStarred = prev.data.has(oracleId);
        const newData = new Map(prev.data);
        if (isCurrentlyStarred) {
          newData.delete(oracleId);
        } else {
          newData.set(oracleId, {
            oracle_id: oracleId,
            name,
            scryfall_id: scryfallId,
            starred_at: { seconds: Date.now() / 1000, nanoseconds: 0 } as never,
          });
        }
        return { ...prev, data: newData };
      });

      try {
        await toggleStar(uid, oracleId, name, isCurrentlyStarred, scryfallId);
      } catch (error) {
        // Revert on error
        setState((prev) => {
          const newData = new Map(prev.data);
          if (isCurrentlyStarred) {
            newData.set(oracleId, {
              oracle_id: oracleId,
              name,
              scryfall_id: scryfallId,
              starred_at: { seconds: Date.now() / 1000, nanoseconds: 0 } as never,
            });
          } else {
            newData.delete(oracleId);
          }
          return { ...prev, data: newData };
        });
        console.error('Failed to toggle star:', error);
      }
    },
    [setState],
  );
}

/**
 * Check if a specific card is starred.
 * Uses stable starredOracleIdsAtom to avoid unnecessary re-renders.
 */
export function useIsStarred(oracleId: string | undefined): boolean {
  const starredIds = useAtomValue(starredOracleIdsAtom);
  return oracleId ? starredIds.has(oracleId) : false;
}

/**
 * Get the full starred data map.
 */
export function useStarredData(): Map<string, StarredCard> {
  const [{ data }] = useAtom(starredStateAtom);
  return data;
}

/**
 * Hook to update the display edition (scryfall_id) of a starred card.
 * Uses optimistic updates for instant feedback.
 */
export function useUpdateStarredEdition() {
  const [, setState] = useAtom(starredStateAtom);

  return useCallback(
    async (oracleId: string, scryfallId: string) => {
      const uid = auth.currentUser?.uid;
      if (!uid) return;

      // Optimistic update
      setState((prev) => {
        const newData = new Map(prev.data);
        const existing = newData.get(oracleId);
        if (existing) {
          newData.set(oracleId, { ...existing, scryfall_id: scryfallId });
        }
        return { ...prev, data: newData };
      });

      try {
        await updateStarredEdition(uid, oracleId, scryfallId);
      } catch (error) {
        // Firestore subscription will revert on error
        console.error('Failed to update starred edition:', error);
        throw error;
      }
    },
    [setState],
  );
}
